import { registerPlugin, Capacitor } from "@capacitor/core";
import type { BackgroundGeolocationPlugin } from "@capacitor-community/background-geolocation";
import { Geolocation } from "@capacitor/geolocation";
import { doc, setDoc, updateDoc, serverTimestamp } from "firebase/firestore";
import { db, crashlytics } from "./firebase";

const BackgroundGeolocation = registerPlugin<BackgroundGeolocationPlugin>(
  "BackgroundGeolocation",
);

type LiveLocationShareInput = {
  chatId: string;
  shareId: string;
  userId: string;
  expiresAt: string;
  label?: string;
  placeName?: string;
};

type ActiveShareState = {
  chatId: string;
  shareId: string;
  userId: string;
  mode: "native" | "web";
  watcherId: string;
  stopTimeout: ReturnType<typeof setTimeout> | null;
};

let activeShare: ActiveShareState | null = null;

const writeLiveLocationUpdate = async (
  input: LiveLocationShareInput,
  coords: { latitude: number; longitude: number; accuracyMeters?: number },
) => {
  if (!db) return;

  await setDoc(
    doc(db, "chats", input.chatId, "liveLocations", input.shareId),
    {
      chatId: input.chatId,
      senderId: input.userId,
      latitude: coords.latitude,
      longitude: coords.longitude,
      ...(typeof coords.accuracyMeters === "number"
        ? { accuracyMeters: coords.accuracyMeters }
        : {}),
      ...(input.label ? { label: input.label } : {}),
      ...(input.placeName ? { placeName: input.placeName } : {}),
      expiresAt: input.expiresAt,
      isActive: true,
      updatedAt: serverTimestamp(),
    },
    { merge: true },
  );
};

const clearActiveShareState = () => {
  if (activeShare?.stopTimeout) {
    clearTimeout(activeShare.stopTimeout);
  }
  activeShare = null;
};

export const stopChatLiveLocationShare = async (params?: {
  chatId?: string;
  shareId?: string;
  userId?: string;
  markStopped?: boolean;
}) => {
  const current = activeShare;
  if (!current) return;

  try {
    if (current.mode === "native") {
      await BackgroundGeolocation.removeWatcher({ id: current.watcherId });
    } else {
      await Geolocation.clearWatch({ id: current.watcherId });
    }
  } catch (error) {
    crashlytics.logError(
      `Failed to stop live location watcher: ${JSON.stringify((error as Error)?.message || error)}`,
      "chatLiveLocationService.ts",
      error as Error,
    );
  }

  if (params?.markStopped !== false && db) {
    try {
      await updateDoc(doc(db, "chats", current.chatId, "liveLocations", current.shareId), {
        isActive: false,
        endedAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });
    } catch (error) {
      crashlytics.logError(
        `Failed to mark live location inactive: ${JSON.stringify((error as Error)?.message || error)}`,
        "chatLiveLocationService.ts",
        error as Error,
      );
    }
  }

  clearActiveShareState();
};

export const startChatLiveLocationShare = async (input: LiveLocationShareInput) => {
  const isNative = Capacitor.isNativePlatform();

  if (
    activeShare &&
    activeShare.chatId === input.chatId &&
    activeShare.shareId === input.shareId &&
    activeShare.userId === input.userId
  ) {
    return;
  }

  if (activeShare) {
    await stopChatLiveLocationShare({ markStopped: false });
  }

  const expiresAtMs = new Date(input.expiresAt).getTime();
  const remainingMs = expiresAtMs - Date.now();
  if (!Number.isFinite(remainingMs) || remainingMs <= 0) {
    throw new Error("Live location expiration is invalid");
  }

  if (isNative) {
    const watcherId = await BackgroundGeolocation.addWatcher(
      {
        requestPermissions: true,
        stale: false,
        distanceFilter: 20,
        backgroundTitle: "Live location sharing",
        backgroundMessage: "Updating your live location in chat.",
      },
      async (location, error) => {
        if (error) {
          crashlytics.logError(
            `Live location background error: ${JSON.stringify(error)}`,
            "chatLiveLocationService.ts",
            error,
          );
          return;
        }

        const coords = location?.coords;
        if (!coords) return;

        try {
          await writeLiveLocationUpdate(input, {
            latitude: coords.latitude,
            longitude: coords.longitude,
            accuracyMeters: coords.accuracy,
          });
        } catch (error) {
          crashlytics.logError(
            `Live location write failed: ${JSON.stringify((error as Error)?.message || error)}`,
            "chatLiveLocationService.ts",
            error as Error,
          );
        }
      },
    );

    activeShare = {
      chatId: input.chatId,
      shareId: input.shareId,
      userId: input.userId,
      mode: "native",
      watcherId,
      stopTimeout: setTimeout(() => {
        void stopChatLiveLocationShare({ markStopped: true });
      }, remainingMs),
    };
    return;
  }

  const permissionStatus = await Geolocation.checkPermissions();
  if (
    permissionStatus.location !== "granted" &&
    permissionStatus.coarseLocation !== "granted"
  ) {
    const requestStatus = await Geolocation.requestPermissions({
      permissions: ["location", "coarseLocation"],
    });
    if (
      requestStatus.location !== "granted" &&
      requestStatus.coarseLocation !== "granted"
    ) {
      throw new Error("Location permission denied");
    }
  }

  const watcherId = await Geolocation.watchPosition(
    {
      enableHighAccuracy: true,
      timeout: 15000,
      maximumAge: 5000,
      minimumUpdateInterval: 5000,
    },
    async (position, error) => {
      if (error) {
        crashlytics.logError(
          `Live location web error: ${JSON.stringify(error)}`,
          "chatLiveLocationService.ts",
          error,
        );
        return;
      }

      if (!position?.coords) return;

      try {
        await writeLiveLocationUpdate(input, {
          latitude: position.coords.latitude,
          longitude: position.coords.longitude,
          accuracyMeters: position.coords.accuracy,
        });
      } catch (writeError) {
        crashlytics.logError(
          `Live location write failed: ${JSON.stringify((writeError as Error)?.message || writeError)}`,
          "chatLiveLocationService.ts",
          writeError as Error,
        );
      }
    },
  );

  activeShare = {
    chatId: input.chatId,
    shareId: input.shareId,
    userId: input.userId,
    mode: "web",
    watcherId,
    stopTimeout: setTimeout(() => {
      void stopChatLiveLocationShare({ markStopped: true });
    }, remainingMs),
  };
};

export const getActiveChatLiveLocationShareId = () => activeShare?.shareId || null;